import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { oesApi } from '../services/api';
import Layout from '../components/layout/Layout';
import RadarChart from '../components/RadarChart';

// Bar that animates from 0 to pct on mount (progressive fill)
function AnimatedBar({ pct, color }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setWidth(pct), 50);
    return () => clearTimeout(id);
  }, [pct]);
  return (
    <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700 ease-out"
        style={{ width: `${width}%`, backgroundColor: color }}
      />
    </div>
  );
}

export default function OESQuestionnaireResult() {
  const navigate = useNavigate();
  const { oesId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [oes, setOes] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [res, oesRes] = await Promise.all([
          oesApi.getQuestionnaireResult(Number(oesId)),
          oesApi.getById(Number(oesId)).catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setResult(res.data);
          setOes(oesRes?.data ?? null);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load result');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [oesId]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center text-slate-600">Loading result…</div>
      </Layout>
    );
  }

  if (error || !result) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <p className="text-red-600 mb-4">{error || 'No result'}</p>
          <Link to="/dashboard" className="text-eucnet-sky hover:underline">Back to dashboard</Link>
        </div>
      </Layout>
    );
  }

  const { gen_compliance, progress, by_domain, submitted_at } = result;
  
  // Sort domains: use data order (frameworks return domains in their natural order)
  const domains = Object.entries(by_domain || {});
  
  // Color coding based on compliance score
  const getComplianceColor = (score) => {
    if (score >= 80) return '#10b981'; // Green for high compliance (80-100%)
    if (score >= 50) return '#f59e0b'; // Amber/yellow for medium compliance (50-79%)
    return '#ef4444'; // Red for low compliance (0-49%)
  };

  const logoSrc = oes?.logo ? (oes.logo.startsWith('data:') ? oes.logo : `data:image/jpeg;base64,${oes.logo}`) : null;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center mb-4 pb-3 border-b border-slate-200">
          {logoSrc && (
            <img
              src={logoSrc}
              alt=""
              className="w-20 h-20 object-contain mr-3 flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div>
            <h2 className="text-xl font-bold text-eucnet-navy">{oes?.name || 'OES'}</h2>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-eucnet-navy mb-2">Posture questionnaire result</h1>
        {submitted_at && (
          <p className="text-sm text-slate-500 mb-6">
            Submitted {new Date(submitted_at).toLocaleString()}
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <p className="text-sm font-medium text-slate-600 mb-1">Overall compliance</p>
            <div className="relative inline-flex items-center justify-center w-32 h-32 rounded-full bg-slate-100">
              <svg className="w-32 h-32 -rotate-90" viewBox="0 0 36 36">
                <path
                  fill="none"
                  stroke="#e2e8f0"
                  strokeWidth="3"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  fill="none"
                  stroke={getComplianceColor(gen_compliance)}
                  strokeWidth="3"
                  strokeDasharray={`${gen_compliance}, 100`}
                  strokeLinecap="round"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <span className="absolute text-2xl font-bold text-eucnet-navy">{gen_compliance}%</span>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <p className="text-sm font-medium text-slate-600 mb-1">Progress (assessed)</p>
            <div className="relative inline-flex items-center justify-center w-32 h-32 rounded-full bg-slate-100">
              <svg className="w-32 h-32 -rotate-90" viewBox="0 0 36 36">
                <path
                  fill="none"
                  stroke="#e2e8f0"
                  strokeWidth="3"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="3"
                  strokeDasharray={`${progress}, 100`}
                  strokeLinecap="round"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <span className="absolute text-2xl font-bold text-eucnet-navy">{progress}%</span>
            </div>
          </div>
        </div>

        {domains.length > 0 && (
          <>
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-eucnet-navy mb-4">Compliance by function</h2>
              <div className="flex justify-center">
                <RadarChart
                  data={Object.fromEntries(domains.map(([domain, data]) => [domain, data.pct]))}
                  size={400}
                />
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-eucnet-navy mb-4">By domain</h2>
              <div className="space-y-4">
                {domains.map(([domain, data]) => (
                  <div key={domain}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-slate-700">{domain}</span>
                      <span className="text-slate-600">{data.pct}% compliant</span>
                    </div>
                    <AnimatedBar pct={data.pct} color={getComplianceColor(data.pct)} />
                  </div>
                ))}
              </div>
            </div>

          </>
        )}

        <div className="mt-8 flex gap-3">
          <Link
            to={`/oes/${oesId}/risk-matrix`}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Risk matrix
          </Link>
          <Link
            to={`/oes/${oesId}/questionnaire`}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Edit questionnaire
          </Link>
          <Link
            to="/dashboard"
            className="px-4 py-2 rounded-lg bg-eucnet-sky text-white hover:bg-eucnet-navy"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    </Layout>
  );
}
